import axios from "axios";
import { gql } from "@apollo/client";
/**
 * Function to upload a file using the REST API.
 * @param {File} file - The file to be uploaded.
 * @param {string} fileType - The file type to upload (e.g., "policy", "check_evidence", "control_evidence").
 * @param {string} relatedEntityId - Policy ID, Check ID, or Control ID.
 * @param {string} version - The version of the file.
 * @param {string} accessToken - The access token for Authorization.
 * @param {string} idToken - The ID token for Authorization.
 * @param {boolean} isCreate - Whether the file is being created or updated.
 * @returns {Promise} - Axios response.
 */
export const uploadFile = async (
  file,
  fileType,
  relatedEntityId,
  version,
  accessToken,
  idToken,
  ownerEmployeeId,
  isCreate,
  justification
) => {
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/file/upload`;
  const formData = new FormData();
  formData.append("file", file);
  if (version) formData.append("version", version);
  formData.append("fileType", fileType);

  // Append the appropriate ID based on the fileType
  if (fileType === "policy") {
    formData.append("policy_id", relatedEntityId);
  } else if (fileType === "check_evidence") {
    formData.append("checkId", relatedEntityId);
  } else if (fileType === "control_evidence") {
    formData.append("controlId", relatedEntityId);
  } else if (fileType === "pass_with_evidence") {
    formData.append("checkId", relatedEntityId);
    if (justification) formData.append("justification", justification);
  } else if (fileType === "employee_resume") {
    formData.append("checkId", relatedEntityId);
    formData.append("owner_employee_id", ownerEmployeeId);
  } else if (fileType === "audit_document") {
    formData.append("audit_id", relatedEntityId);
  }

  // Add mode if it's not a create operation
  if (!isCreate) {
    formData.append("mode", "update");
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    id_token: idToken,
  };

  try {
    const response = await axios.post(url, formData, { headers });
    return response.data;
  } catch (error) {
    console.error("File upload failed:", error);
    throw error;
  }
};

/**
 * Soft-delete a file by its UUID. Server sets is_deleted = true.
 */
export const deleteFile = async (fileUuid, accessToken, idToken) => {
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/file/${encodeURIComponent(fileUuid)}`;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    id_token: idToken,
  };
  try {
    const response = await axios.delete(url, { headers });
    return response.data;
  } catch (error) {
    console.error("File delete failed:", error);
    throw error;
  }
};

export const GET_FILES_BY_TYPE = gql`
  query GetFilesByType($fileType: String!) {
    getFilesByType(fileType: $fileType) {
      fileType
      files {
        fileId
        maskName
        path
        isFileValid
        fileRejectReason
        ownerEmployeeId
        checkId
        createdAt
        updatedAt
        createdByEmployeeId
      }
    }
  }
`;
